import * as React from 'react';
import {
  Grid, Box, Typography, Card, Paper, CardHeader, Button, CardContent, Divider, TextField, CircularProgress,
} from '@material-ui/core';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { Skeleton, Autocomplete } from '@material-ui/lab';
import {
  Cached,
  CalendarToday, // Pause, Timer,
} from '@material-ui/icons';
import { FilterTypes } from './WorkViewInterfaces';
import type {
  ICompany, IWorkViewDetail, IWorkViewPrimaryGroup, IWorkViewFilterData,
  IFilter,
} from './WorkViewInterfaces';
import HorizontalLayout from './layouts/HorizontalLayout';
import VerticalLayout from './layouts/VerticalLayout';
import type { ILabelValuePair } from '../../marking/MarkingInterfaces';
import ChipSelect from '../../ChipSelect';
import StatusCard from './StatusCard';
import globalUseStyles from '../../../globalClasses';
import ColumnerLayout from './layouts/ColumnerLayout';

interface IWorkViewProp {
  workViewId: string;
  refreshWorkView: boolean;
  waitForFilterData: boolean;
  setWaitForFilterData: (wait: boolean) => void;
}

const WorkView: React.FC<IWorkViewProp> = ({
  workViewId, refreshWorkView, waitForFilterData, setWaitForFilterData,
}) => {
  // Initial imports
  const { enqueueSnackbar } = useSnackbar();
  const [workView, setWorkView] = React.useState<IWorkViewDetail>();
  const [primaryGroups, setPrimaryGroups] = React.useState<IWorkViewPrimaryGroup[]>();
  const [layoutStyle, setLayoutStyle] = React.useState<ILabelValuePair>();
  const [company, setCompany] = React.useState<ICompany | null>(null);
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [filterData, setFilterData] = React.useState<IWorkViewFilterData | undefined>();
  const globalStyles = globalUseStyles();

  const [selectedFilter, setSelectedFilter] = React.useState<IFilter>({ label: 'Total', value: 1, color: 'blue' });

  // Fetcher to fetch the Companies
  const fetcher = (url: string) => axios.get<ICompany[]>(url)
    .then((response) => response.data);

  const { data: AllCompanies } = useSWR<ICompany[]>(
    ['/massreceive/getcompanies'],
    (url) => fetcher(url),
  );

  const handleFilterData = (filterType: IFilter, companyName: string | undefined) => {
    setIsFetching(true);
    setWaitForFilterData(true);
    axios.post<IWorkViewFilterData>('/workView/FilterWorkView', {
      filterType,
      workViewId,
      companyName,
    })
      .then((result) => {
        if (result.data) {
          setFilterData(result.data);
        }
        return result.data;
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar('Unable to Filter Work View.', { persist: false, variant: 'error' });
        }
        setIsFetching(false);
      })
      .finally(() => {
        setIsFetching(false);
        setWaitForFilterData(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFetchWorkView = (url: string) => {
    setIsFetching(true);
    setWaitForFilterData(true);
    axios.get<IWorkViewDetail>(url, {
      headers: { 'Content-Type': 'application/json' },
      params: {
        workViewId,
      },
    })
      .then((result) => {
        if (result.data) {
          setWorkView(result.data);
          setPrimaryGroups(result.data.primaryGroups);
          setLayoutStyle(result.data.layoutStyle);
        }
        return result.data;
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar('Unable to find Work View.', { persist: false, variant: 'error' });
        }
        setIsFetching(false);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data, isValidating } = useSWR(
    ['/workView/GetWorkViewDetail'],
    (url) => handleFetchWorkView(url),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
    },
  );

  const handleSelectFilterItem = (filterType: IFilter) => {
    setSelectedFilter(filterType);
  };

  const handleRefineResults = () => {
    handleFilterData(selectedFilter, company?.companyName);
  };

  const handleClearFilter = () => {
    setSelectedFilter({ label: 'Total', value: 1, color: 'blue' });
    setCompany(null);
  };

  // This function will set the number of columns.
  const getColumnerCols = (columnerType: string) => {
    const selectedColumn = parseInt(columnerType.replace('Columner - ', ''), 10);
    if (selectedColumn) {
      return selectedColumn;
    }
    return 6;
  };

  React.useEffect(() => {
    if (workViewId) {
      const url = '/workView/GetWorkViewDetail';
      handleFetchWorkView(url);
      handleFilterData(selectedFilter, company?.companyName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workViewId, refreshWorkView]);

  // Render
  return (
    <>
      {
        isFetching ? (
          <Grid container alignItems="center" justifyContent="center" style={{ minHeight: '50vh' }}>
            <CircularProgress size={75} />
          </Grid>
        )
          : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid item xs={12}>
                    <Box px={2} py={3}>
                      <Typography variant="h6">
                        {workView?.name}
                      </Typography>
                      <Typography variant="caption" color="primary">
                        {workView?.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item>
                      <StatusCard
                        iconColor="green"
                        secondaryText="Total Items"
                        primaryText={filterData ? filterData.totalItems.toString() : '0'}
                        icon={<CalendarToday className={globalStyles.smallIconSize} />}
                      />
                    </Grid>
                    <Grid item>
                      <StatusCard
                        iconColor="red"
                        secondaryText="Shipping Log Items"
                        primaryText={filterData ? filterData.shipLoggedItems.toString() : '0'}
                        icon={<LocalShippingIcon className={globalStyles.smallIconSize} />}
                      />
                    </Grid>
                    {/* <Grid item>
                      <StatusCard
                        iconColor="orange"
                        secondaryText="Late Items"
                        primaryText={filterData ? filterData.lateItems.toString() : '0'}
                        icon={<Timer className={globalStyles.smallIconSize} />}
                      />
                    </Grid>
                    <Grid item>
                      <StatusCard
                        iconColor="yellow"
                        secondaryText="Stalled Items"
                        primaryText={filterData ? filterData.stalledItems.toString() : '0'}
                        icon={<Pause className={globalStyles.smallIconSize} />}
                      />
                    </Grid> */}
                    <Grid item>
                      <StatusCard
                        iconColor="maroon"
                        secondaryText="Rejected in View"
                        primaryText={filterData ? filterData.rejectedItems.toString() : '0'}
                        icon={<Cached className={globalStyles.smallIconSize} />}
                      />
                    </Grid>
                  </Grid>
                  <br />
                  <Grid xs={12}>
                    <Card>
                      <Paper>
                        <CardHeader title="Refine Results" />
                        <Divider variant="fullWidth" />
                        <CardContent>
                          <Grid item>
                            <Grid item xs={12}>
                              <Box pl={2} pb={2}>
                                <Typography variant="h6">{'Filter Types'}</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Grid item xs={12} container spacing={0}>
                                {FilterTypes.length > 1 && FilterTypes.map((filter) => (
                                  <Grid item>
                                    <Box pl={1.5} p={0.5}>
                                      <ChipSelect
                                        key={filter.value}
                                        label={filter.label}
                                        selected={selectedFilter?.value === filter.value}
                                        handleClick={() => handleSelectFilterItem(filter)}
                                      />
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Grid>
                            <Grid item xs={6}>
                              <Box pl={4} pb={4}>
                                {
                                  !AllCompanies
                                    ? <Skeleton><TextField fullWidth /></Skeleton>
                                    : (
                                      <Autocomplete
                                        value={company}
                                        onChange={(event: unknown, selectedCompany: ICompany | null) => {
                                          const updatedCompany = selectedCompany || { companyId: '', companyName: '' };
                                          setCompany(updatedCompany);
                                        }}
                                        getOptionSelected={(option: ICompany, value: ICompany) => option?.companyId === value?.companyId}
                                        autoHighlight
                                        autoSelect
                                        disabled={false}
                                        options={AllCompanies}
                                        getOptionLabel={(option) => option?.companyName?.toString()}
                                        id="company-select"
                                        // prop spreading is directly from MUI tsx example
                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                        renderInput={(params) => (<TextField autoFocus={company?.companyName === null} {...params} label="Company" variant="outlined" />)}
                                      />
                                    )
                                }
                              </Box>
                              <Grid item container spacing={3} justifyContent="flex-end">
                                <Box m={2}>
                                  <Grid item xs={12}>
                                    <Box display="inline" mx={4}>
                                      <Button
                                        variant="text"
                                        color="secondary"
                                        onClick={() => handleClearFilter()}
                                      >
                                        {'Clear'}
                                      </Button>
                                    </Box>
                                    <Box display="inline" mx={4}>
                                      <Button disabled={false} variant="contained" color="primary" onClick={handleRefineResults}>
                                        {'Refine Results'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Paper>
                    </Card>
                  </Grid>
                  <br />
                  <br />
                  <br />
                  <br />
                  <Grid xs={12}>
                    {
                      primaryGroups && filterData && layoutStyle?.label === 'Horizontal' && !waitForFilterData && primaryGroups.length === filterData.primaryGroupData.length && (
                        <HorizontalLayout
                          primaryGroups={primaryGroups}
                          primaryGroupData={filterData.primaryGroupData}
                          filter={filterData.filterType}
                        />
                      )
                    }
                    {
                      primaryGroups && filterData && layoutStyle?.label === 'Vertical' && !waitForFilterData && primaryGroups.length === filterData.primaryGroupData.length && (
                        <VerticalLayout
                          primaryGroups={primaryGroups}
                          primaryGroupData={filterData.primaryGroupData}
                          filter={filterData.filterType}
                        />
                      )
                    }
                    {
                      primaryGroups && filterData && layoutStyle?.label.includes('Columner') && !waitForFilterData && primaryGroups.length === filterData.primaryGroupData.length && (
                        <ColumnerLayout
                          primaryGroups={primaryGroups}
                          columnerValue={getColumnerCols(layoutStyle?.label)}
                          primaryGroupData={filterData.primaryGroupData}
                          filter={filterData.filterType}
                        />
                      )
                    }
                  </Grid>
                </Grid>
              </Grid>
            </>
          )
      }
    </>
  );
};

export default WorkView;
